// src/lib/templates.js
// Utility for per-org communication templates (Email/SMS) + placeholder filling.

export const DEFAULT_TEMPLATES = {
  pm: {
    emailSubjectTemplate: 'Quick follow-up — {{customerName}} (Lead {{leadId}})',
    emailBodyTemplate: [
      'Hi {{firstName}},',
      '',
      'Just checking whether we can help you move forward.',
      'If a better price or an alternative spec helps, reply and I’ll review it.',
      '',
      'Thanks,',
      '{{senderName}}',
      '{{orgSignature}}',
    ].join('\n'),
    defaultRecipients: '',
    smsTemplate:
      'Hi {{firstName}}, we can revisit price/spec to help you move forward. Reply here or call {{orgPhone}}.',
  },
  cancelled: {
    emailSubjectTemplate: 'Sorry to hear — can we help, {{customerName}}? (Lead {{leadId}})',
    emailBodyTemplate: [
      'Hi {{firstName}},',
      '',
      'Sorry to hear you cancelled. If pricing/spec or timings were the blocker,',
      'we can review options or improve our offer.',
      '',
      'Best,',
      '{{senderName}}',
      '{{orgSignature}}',
    ].join('\n'),
    defaultRecipients: '',
    smsTemplate:
      'Hi {{firstName}}, we can review price/spec or timings if helpful. Reply here or call {{orgPhone}}.',
  },
  callbacks: {
    emailSubjectTemplate: 'Quick check-in — {{customerName}} (Lead {{leadId}})',
    emailBodyTemplate: [
      'Hi {{firstName}},',
      '',
      'We tried to reach you to help with next steps.',
      'If you want us to revisit price/spec or book a new appointment, just reply.',
      '',
      'Best,',
      '{{senderName}}',
      '{{orgSignature}}',
    ].join('\n'),
    defaultRecipients: '',
    smsTemplate:
      'Hi {{firstName}}, quick check-in — we can revisit price/spec or arrange a new appointment. Reply here or call {{orgPhone}}.',
  },
  survey: {
    emailSubjectTemplate: 'Survey request — {{customerName}} (Lead {{leadId}}) {{postcode}}',
    emailBodyTemplate: [
      'Survey requested for {{customerName}} ({{postcode}})',
      '',
      'Preferred date: {{surveyDate}}',
      'Stage: {{stage}}',
      '',
      'Products: {{products}}',
      'Price: {{price}}  Deposit: {{deposit}}',
      'Attachments: {{attachments}}',
      '',
      'Notes: {{notes}}',
      '',
      'Thanks,',
      '{{senderName}}',
      '{{orgSignature}}',
    ].join('\n'),
    defaultRecipients: '',
    // no SMS for survey
  },
};

/** Fill {{placeholders}} in a string with values from vars. */
export function fill(templateStr, vars) {
  if (!templateStr || typeof templateStr !== 'string') return '';
  return templateStr.replace(/{{\s*([\w\.]+)\s*}}/g, (_, key) => {
    const v = vars && key in vars ? vars[key] : '';
    return v == null ? '' : String(v);
  });
}

/** Compact helpers */
export function formatDateShort(date) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  } catch {
    return '';
  }
}
export function formatTime24(date) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mi}`;
  } catch {
    return '';
  }
}

/** Build the placeholder map from a lead + org profile + extra data */
export function buildVars({ lead = {}, org = {}, report = {}, extras = {} }) {
  const get = (obj, k) => (obj && obj[k] != null ? obj[k] : '');
  const tsToDate = (ts) =>
    ts && typeof ts.toDate === 'function' ? ts.toDate() : ts ? new Date(ts) : null;
  const apptStart = tsToDate(lead.apptStartAt);

  // --- NEW: Dynamic sender and conditional organization signature ---
  const currentUser = extras.user || {};
  const senderName = get(currentUser, 'displayName') || '';

  const orgName = get(org, 'companyName') || get(org, 'name') || '';
  const orgPhone = get(org, 'phone') || '';
  const orgSignature = orgPhone ? `${orgName} | ${orgPhone}` : orgName;
  // ---

  const surveyDate = get(report, 'survey?.date') || get(report?.survey || {}, 'date') || '';
  const surveySlot = get(lead, 'surveySlot') || '';
  const surveyor = get(lead, 'surveyorName') || 'TBC';
  const specialArrangements = get(report, 'specialArrangements') || 'None';

  const nameFromParts = `${get(lead, 'customerFirstName')} ${get(lead, 'customerLastName')}`.trim();
  const customerName = get(lead, 'customerFullName') || nameFromParts || '';

  const vars = {
    leadId: get(lead, 'id') || get(lead, 'leadId') || '',
    customerTitle: get(lead, 'customerTitle') || '',
    firstName: get(lead, 'customerFirstName') || '',
    lastName: get(lead, 'customerLastName') || '',
    customerName,
    email: get(lead, 'customerEmail') || '',
    phone: get(lead, 'customerPhone') || '',
    customerPhone: get(lead, 'customerPhone') || '',
    postcode: get(lead, 'postcode') || '',
    address: get(lead, 'addressLine1') || get(lead, 'address') || '',
    salespersonName: get(lead, 'salespersonName') || get(lead, 'salespersonId') || '',
    orgEmail: get(org, 'email') || '',
    apptDate: apptStart ? formatDateShort(apptStart) : '',
    apptTime: apptStart ? formatTime24(apptStart) : '',
    pmProducts: get(lead, 'pmProducts') || '',
    pmPrice: get(lead, 'pmPrice') || '',
    pmReason: get(lead, 'pmReason') || '',
    surveyDate: surveyDate || '',
    stage: get(lead, 'postSaleStage') || '',
    products: get(report, 'products') || get(lead, 'products') || '',
    price: get(report, 'price') || get(lead, 'price') || '',
    deposit: get(report, 'deposit') || get(lead, 'deposit') || '',
    attachments: get(extras, 'attachments') || '',
    notes: get(report, 'notes') || get(lead, 'adminNotes') || '',
    surveyor: surveyor,
    surveySlot: surveySlot,
    specialArrangements: specialArrangements,
    productsSummary: get(report, 'products') || get(lead, 'products') || 'See Report',
    doorNotes: get(report, 'doorNotes') || 'None',
    leadLink: get(extras, 'leadLink') || '',

    // Add the new dynamic placeholders
    senderName: senderName,
    orgSignature: orgSignature,
  };
  return { ...vars, ...(extras || {}) };
}


export function normalizeRecipients(defaultRecipients, customerEmail) {
  const list = [];
  const add = (s) => {
    if (!s) return;
    for (const r of String(s)
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)) {
      if (!list.includes(r)) list.push(r);
    }
  };
  add(defaultRecipients);
  add(customerEmail);
  return list;
}

export function makeMailtoUrl(recipients, subject, body) {
  const to = encodeURIComponent(recipients.join(', ')).replace(/%20/g, ' ');
  const sub = encodeURIComponent(subject || '').replace(/%20/g, ' ');
  const b = encodeURIComponent(body || '').replace(/%20/g, ' ');
  return `mailto:${to}?subject=${sub}&body=${b}`;
}